import { reactiveSetArray, set } from '../../../services/util/store.helper';
import HourService from '../../../services/api/hour.service';

export default {
  namespaced: true,
  state: {
    loading: false,
    currentTime: null,
    serverTime: null,
    currentShift: null,
    selectedCell: null,
    currentHour: null,
    displayHour: null,
    currentDate: null,
    selectedView: 'shift',
    machines: [],
    cells: [],
    shifts: [],
    machineStatus: [],

    theme: {
      isDark: true,
      themes: {
        dark: {
          cardbg: '#15191E',
          subtitlecolor: '#A1A3A5',
          titlecolor: '#FFFFFF',
          error: '#ED5353',
          success: '#21C77C',
        },
        light: {
          cardbg: '#FFFFFF',
          subtitlecolor: '#333333',
          titlecolor: '#15191E',
          error: '#FF5252',
          success: '#21C77C',
        },
      },
    },
    userId: '',
    customerId: '',
    username: '',
    dashboardreport: '',
    dataFromJSON: false,
    intervalfetch: 50,
    configDrawer: false,
    config: {
      greaterthangreen: 80,
      lessthanred: 70,
      colors: {
        safe: '#21CE99',
        warning: '#FFDA6B',
        danger: '#ED5353',
      },
    },
  },

  mutations: {
    setLoading: set('loading'),
    setCurrentTime: set('currentTime'),
    setServerTime: set('serverTime'),
    setCurrentShift: set('currentShift'),
    setCurrentHour: set('currentHour'),
    setDisplayHour: set('displayHour'),
    setCurrentDate: set('currentDate'),
    setSelectedCell: set('selectedCell'),
    setSelectedView: set('selectedView'),
    setMachines: set('machines'),
    setMachine: reactiveSetArray('machines'),
    setCells: set('cells'),
    setShifts: set('shifts'),
    setMachineStatus: set('machineStatus'),

    setTheme: set('theme'),
    setUserId: set('userId'),
    setCustomerId: set('customerId'),
    setUsername: set('username'),
    setDashboardReport: set('dashboardreport'),
    setDataFromJSON: set('dataFromJSON'),
    setConfigJson: set('config'),
    setIntervalFetch: set('intervalfetch'),
    setConfigDrawer: set('configDrawer'),
  },

  actions: {
    getServerTime: async ({ commit }) => {
      try {
        const { data } = await HourService.getCurrentTime();
        if (data && data.results) {
          commit('setCurrentTime', data.results);
          commit('setServerTime', data.results);
          return data.results;
        }
        return false;
      } catch (e) {
        console.error('Error fetching server time:', e);
        return false;
      }
    },
    getBusinessTime: async ({ commit, dispatch, state }) => {
      const toYYYYMMDD = (value) => {
        if (value === null || value === undefined) return null;
        const raw = typeof value === 'string' ? value.trim() : value;

        if (typeof raw === 'number' || /^\d+$/.test(String(raw))) {
          const rawStr = String(raw);
          if (rawStr.length === 8) {
            return parseInt(rawStr, 10);
          }
          const parsed = new Date(Number(raw));
          if (!Number.isNaN(parsed.getTime())) {
            return parseInt(
              `${parsed.getFullYear()}${String(parsed.getMonth() + 1).padStart(2, '0')}${String(parsed.getDate()).padStart(2, '0')}`,
              10,
            );
          }
        } else {
          const parsed = new Date(raw);
          if (!Number.isNaN(parsed.getTime())) {
            return parseInt(
              `${parsed.getFullYear()}${String(parsed.getMonth() + 1).padStart(2, '0')}${String(parsed.getDate()).padStart(2, '0')}`,
              10,
            );
          }
        }

        return null;
      };

      let serverTime = await dispatch('getServerTime');

      if (!serverTime) serverTime = Date.now();

      try {
        const data = await dispatch('calendar/getBusinessTime', serverTime, { root: true });
        if (data && data.date) {
          const businessDate = toYYYYMMDD(data.date)
            || toYYYYMMDD(serverTime)
            || toYYYYMMDD(Date.now());
          const newShift = data.shiftName || 'Shift 1';

          if (state.currentShift !== newShift) {
            commit('setCurrentShift', newShift);
          }
          if (businessDate && state.currentDate !== businessDate) {
            commit('setCurrentDate', businessDate);
          }
          if (data.hour) commit('setCurrentHour', data.hour);
          if (data.displayHour) commit('setDisplayHour', data.displayHour);
        } else {
          throw new Error('Calendar data is empty');
        }
        return true;
      } catch (e) {
        const fallbackDate = toYYYYMMDD(serverTime) || toYYYYMMDD(Date.now());
        if (fallbackDate && state.currentDate !== fallbackDate) {
          commit('setCurrentDate', fallbackDate);
        }
        if (state.currentShift !== 'Shift 1') {
          commit('setCurrentShift', 'Shift 1');
        }
        return true;
      }
    },
    getAvailableTime: async ({ state, getters }) => {
      const { selectedView, currentTime } = state;
      const now = currentTime;
      if (selectedView === 'shift') {
        const { shiftStartTime } = getters;
        if (!shiftStartTime) return 0;
        try {
          const { data } = await HourService.getNonWorkingTime(shiftStartTime, now);
          if (data) return now - shiftStartTime - data.results;
        } catch (e) { /* ignore */ }
        return now - shiftStartTime;
      }
      return 0;
    },

    allShifts: async ({ dispatch, commit }) => {
      try {
        const records = await dispatch(
          'element/getRecords',
          { elementName: 'businesshours', query: '?sortquery=sortindex==1' },
          { root: true },
        );
        if (records && records.length) {
          commit('setShifts', records);
          return records;
        }
        return false;
      } catch (e) {
        return false;
      }
    },

    getMachines: async ({ dispatch, commit }, query) => {
      try {
        const records = await dispatch(
          'element/getRecords',
          { elementName: 'machine', query },
          { root: true },
        );
        if (records && records.length) {
          commit('setMachines', records);
          const extractedCells = [...new Set(records.map((m) => m.machinecell).filter(Boolean))];
          commit('setCells', extractedCells);
          return records;
        }
        return false;
      } catch (e) {
        return false;
      }
    },

    getReports: async ({ dispatch }, { reportName, payload }) => {
      try {
        const data = await dispatch(
          'report/executeReport',
          { reportName, payload },
          { root: true },
        );
        if (data && data.reportData) return data.reportData;
        return data || false;
      } catch (e) {
        return false;
      }
    },

    getDashboardReport: async ({ dispatch, commit }, query) => {
      try {
        const records = await dispatch(
          'element/getRecords',
          { elementName: 'machine', query },
          { root: true },
        );
        if (records && records.length) {
          commit('setDashboardReport', records);
          return records;
        }
        return false;
      } catch (e) {
        return false;
      }
    },

    getFileData: async (context, filename) => {
      try {
        const response = await fetch(filename);
        if (!response.ok) return false;

        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) return false;

        const data = await response.json();
        return data;
      } catch (e) {
        console.error('Error fetching JSON data:', e);
        return false;
      }
    },
  },
  getters: {
    firstShift(state) {
      if (state.shifts && state.shifts.length) {
        return state.shifts.filter((s) => s.shiftnumber === 1)[0] || null;
      }
      return null;
    },
    activeTheme(state) {
      return (state.theme && state.theme.isDark) ? 'dark' : 'light';
    },

    filteredMachines: ({ machines, selectedCell }) => {
      if (selectedCell) return machines.filter((m) => m.machinecell === selectedCell);
      return machines;
    },

    shiftStartTime: ({ shifts, currentShift, currentDate }) => {
      if (!shifts.length || !currentShift || !currentDate) return null;
      const shift = shifts.find((s) => s.shiftname === currentShift || s.shift === currentShift);
      if (!shift || !shift.starttime) return null;

      const [hr, min] = shift.starttime.split(':');

      let dateObj;
      if (currentDate.toString().length === 8) {
        const dateStr = currentDate.toString();
        const year = dateStr.substring(0, 4);
        const month = dateStr.substring(4, 6);
        const day = dateStr.substring(6, 8);
        dateObj = new Date(year, month - 1, day);
      } else {
        dateObj = new Date(currentDate);
      }

      dateObj.setHours(parseInt(hr, 10), parseInt(min, 10), 0, 0);
      return dateObj.getTime();
    },
  },
};
